import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { apiGet } from "../../Api";
import { formatDateLocal } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import DelayInput from "../DelayInput";
import withAuth from "../withAuth";
import withTitle from "../withTitle";
import withProps from "../withProps";

interface IQuestionnaireListViewState {
    surveys: any[];
    searchOffset: number;
    searchPerson?: string;
    searchText?: string;

    isLoading: boolean;

    [name: string]: any;
}

interface IQuestionnaireListViewProps extends IAuthProps, RouteComponentPropsHistory<any, any> {
    mode?: "normal" | "digital"
}

const PAGE_SIZE = 50;

class QuestionnaireListView extends React.Component<IQuestionnaireListViewProps, IQuestionnaireListViewState> {
    constructor(props: Readonly<IQuestionnaireListViewProps>) {
        super(props);

        this.state = {
            surveys: [],

            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchPerson: props.location.state ? (((props.location.state.searchPerson === "" && props.authContext.hasRight("survey:read"))) ? props.location.state.searchPerson : "caller") : "",
            searchText: props.location.state ? props.location.state.searchText : "",

            isLoading: false,
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        this.search();
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.surveys.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No questionnaires found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.surveys.map(a => (
                    <tr key={a.id}>
                        <td data-caption="First Name: ">{a.name.first}</td>
                        <td data-caption="Last Name: ">{a.name.last}</td>
                        <td data-caption="Date submitted: ">{formatDateLocal(a.timestamp)}</td>
                        {this.props.mode !== "digital" ? <td data-caption="Health Fair: " className="wrap"><Link to={`/fairs/${a.healthfairId}`}>{a.healthFair.name}</Link></td> : null}
                        <td>
                            <Link className="button" to={this.props.mode === "digital" ? `/digital-surveys/${a.id}` : `/surveys/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }


            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Date Submitted</th>
                            {this.props.mode !== "digital" ? <th>Health Fair</th> : null}
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Search results</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <DelayInput type="search" className="fill-space" placeholder="Search..." timeout={500} name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} />
                    {this.props.mode !== "digital"
                        ? <select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                            <option value="">Everyone</option>
                            <option value="caller">You</option>
                        </select>
                        : null}
                </div>
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.surveys.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }

            const results = await apiGet<any[]>(this.props.mode === "digital"
                ? `survey/digital/search?${parameters.join("&")}`
                : `survey/search?${parameters.join("&")}`);

            this.setState({
                isLoading: false,
                surveys: results,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchText: this.state.searchText,
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }
}

export default withTitle("Questionnaires", withAuth(QuestionnaireListView));
export const DigitalSurveyListView = withTitle("Digital Surveys", withProps(withAuth(QuestionnaireListView), { mode: "digital" }));
